
import { BotRealEstateApi } from '@/api/BotRealEstateApi';
import RealEstateListingsListItem from '@/components/real-estate/RealEstateListingsListItem.vue';
import { GENERIC_ERROR_DESCRIPTION } from '@/constants';
import { BotMixin } from '@/mixins/BotMixin';
import { SelectOption } from '@/types';
import isEqual from 'fast-deep-equal/es6';
import {
  Bot,
  BotDomain,
  FeaturedListingInfo,
  GenericListing,
  GenericListingType,
  RealEstateDomainDataContent,
} from 'ignite360-core';
import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';

@Component({
  name: 'real-estate-listings-list-card',
  components: { RealEstateListingsListItem },
})
export default class RealEstateListingsListCard extends mixins(BotMixin) {
  @Prop({ required: false, type: Boolean, default: false })
  readonly pagination!: boolean;

  @Prop({ required: false, type: Number, default: 20 })
  readonly limit!: number;

  @Prop({ required: false, type: Boolean, default: false })
  readonly scrollable!: boolean;

  selectedListingType: GenericListingType | 'all' = 'all';

  featuredListingInfoList: FeaturedListingInfo[] = [];

  isLoading = false;
  isSubmitting = false;

  page = 1;
  highestLoadedPage = 0;

  loadedListings: GenericListing[] = [];
  hasMore = false;

  get filterOptions(): SelectOption[] {
    return [
      {
        label: 'All',
        value: 'all',
      },
      {
        label: 'For sale',
        value: 'buy',
      },
      {
        label: 'Rent',
        value: 'rent',
      },
      {
        label: 'Sold',
        value: 'sold',
      },
    ];
  }

  get botFeaturedListingInfoList(): FeaturedListingInfo[] | undefined {
    return this.bot.domainData?.[BotDomain.RealEstate]?.featuredListingsList;
  }

  get paginatedListings(): GenericListing[] {
    const startIndex = (this.page - 1) * this.limit;
    return this.loadedListings.slice(startIndex, startIndex + this.limit);
  }

  get canSubmit(): boolean {
    if (!this.botFeaturedListingInfoList && !this.featuredListingInfoList.length) {
      return false;
    }

    return !isEqual(this.botFeaturedListingInfoList, this.featuredListingInfoList);
  }

  async goToNext() {
    this.page++;

    if (this.page > this.highestLoadedPage) {
      await this.loadListings();
    }
  }

  isListingFeatured(listing: GenericListing): boolean {
    return this.featuredListingInfoList.some(
      (info) => info.listingId === listing.listingId && info.source === listing.source,
    );
  }

  onListingFeaturedChange(listing: GenericListing, isFeatured: boolean) {
    const infoIndex = this.featuredListingInfoList.findIndex(
      (info) => info.listingId === listing.listingId && info.source === listing.source,
    );
    if (isFeatured && infoIndex < 0) {
      this.featuredListingInfoList.push({
        listingId: listing.listingId,
        source: listing.source,
      });
    } else if (!isFeatured && infoIndex >= 0) {
      this.featuredListingInfoList.splice(infoIndex, 1);
    }
  }

  async submitChanges() {
    this.isSubmitting = true;
    try {
      await this.$updateBot({
        id: this.bot.id,
        data: {
          domainData: {
            [BotDomain.RealEstate]: {
              ...(this.bot.domainData[BotDomain.RealEstate] || {}),
              featuredListingsList: this.featuredListingInfoList,
            } as RealEstateDomainDataContent,
          },
        },
      });
      this.$notify.success('Successfully updated featured listings');
    } catch (e) {
      this.$notify.error({
        title: 'Updating featured listings failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  goToPrevious() {
    if (this.page <= 1) return;
    this.page--;
  }

  @Watch('bot', { deep: true, immediate: true })
  async onBotChange(bot: Bot, prev?: Bot) {
    if (!prev || bot.id !== prev.id) {
      this.reset();
      await this.loadListings();
    }
  }

  @Watch('selectedListingType')
  async onListingTypeChange() {
    this.reset();
    await this.loadListings();
  }

  private reset() {
    this.featuredListingInfoList = this.botFeaturedListingInfoList?.slice() || [];
    this.page = 1;
    this.loadedListings = [];
  }

  async loadListings(): Promise<void> {
    this.isLoading = true;
    try {
      const filter =
        this.selectedListingType === 'all'
          ? undefined
          : {
              type: this.selectedListingType,
            };

      const listings = await BotRealEstateApi.getListings(this.bot.id, {
        page: this.page,
        limit: this.limit,
        filter,
      });
      this.highestLoadedPage = this.page;
      this.hasMore = listings.length === this.limit;
      this.loadedListings.push(...listings);
    } catch (e) {
      this.$notify.error({
        title: 'Loading listings failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isLoading = false;
    }
  }
}
